import { FormSectionLayout, PlainFormSectionChild, useComputed } from "@homebound/beam";
import { ObjectConfig, ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { Maybe } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { OptionPriceMode } from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { ProjectParams } from "src/routes/routesDef";
import { fail, idsOf, queryResult } from "src/utils";
import { useFormState } from "src/utils/formState";
import {
  SelectOptionsStep_CheckoutConfigFragment,
  SelectOptionsStep_OptionGroupFragment,
  useSelectOptionsStepQuery,
} from "./1.SelectOptionsStep.generated";
import { AddOrModifyOptionsFormInput } from "./addOrModifyOptionsWorkflow";
import { ExteriorOptionCard } from "./components/ExteriorOptionCard";
import { cutoffTag, isExteriorGroup } from "./components/optionCardUtils";
import { OptionsFilterBar } from "./components/OptionsFilterBar";
import { packageOptionCard } from "./components/PackageOptionCard";
import { personalizationOptionCard } from "./components/PersonalizationOptionCard";
import { ProductOptionCard } from "./components/ProductOptionCard";
import { SingleOrLinkedOptionCard } from "./components/SingleOrLinkedOptionCard";
import {
  hasToggle,
  isProductOptionGroup,
  SelectOptionsFormInput,
  showGroup,
  sortGroupOptions,
  toSelectOptionsSections,
} from "./selectOptionsUtils";
import { useSaveOptionSelection } from "./useSaveOptionSelection";

type SelectOptionsStepProps = { formState: ObjectState<AddOrModifyOptionsFormInput> };

/**
 * Step 1 of Add/Modify Options: the project's current configuration as the checkout's sections, each option group on
 * the card its shape calls for. Every pick is auto-saved on the change event, and the reconciled selection the server
 * answers with is what `formState.configChange.optionIds` holds.
 */
export function SelectOptionsStep(props: SelectOptionsStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const configId = useComputed(() => formState.configChange.resultId.value, [formState]);
  const query = useSelectOptionsStepQuery({
    variables: { configId: configId ?? fail("Change event has no result config"), structureId: projectId },
    fetchPolicy: "cache-and-network",
  });
  return queryResult(query, ({ checkoutConfig }) => (
    <SelectOptionsStepView checkoutConfig={checkoutConfig} formState={formState} />
  ));
}

type SelectOptionsStepViewProps = {
  checkoutConfig: SelectOptionsStep_CheckoutConfigFragment;
  formState: ObjectState<AddOrModifyOptionsFormInput>;
};

function SelectOptionsStepView(props: SelectOptionsStepViewProps) {
  const { checkoutConfig, formState } = props;
  const [showExpired, setShowExpired] = useState(false);
  const priceMode = useComputed(() => formState.priceMode.value ?? OptionPriceMode.Price, [formState]);
  // The options' order is fixed on first load, like checkout, so a pick doesn't jump its card to the top of the group
  const [firstSort] = useState(() => sortGroupOptions(checkoutConfig.config.optionGroups));
  const groups = useMemo(
    () => sortGroupOptions(checkoutConfig.config.optionGroups, firstSort),
    [checkoutConfig, firstSort],
  );
  // The cards are locked while a save is in flight, so every pick is made against the server's latest answer
  const { save, saving } = useSaveOptionSelection(formState, groups);
  const optionIds = useComputed(() => formState.configChange.optionIds.value, [formState]);
  const rowsInput = useMemo(() => ({ groups, optionIds }), [groups, optionIds]);

  const rowsForm = useFormState({
    config: formConfig,
    init: { input: rowsInput, map: mapToForm },
    readOnly: saving,
    // Auto-save every pick because only the backend knows how a change cascades: it prunes options whose
    // prerequisites are no longer met or that now conflict, adds required options and personalization defaults (e.g.
    // turning "Add Washer" on picks a default washer), and re-prices the result. Each card's `canBeSelected` /
    // `isSelected` / prices are computed against that saved selection, so the user is always picking against the
    // server's latest answer rather than a stale one.
    autoSave: async () => {
      const toggled = rowsForm.rows.rows
        .filter((row) => row.dirty)
        .flatMap((row) => [row.optionId.originalValue, row.optionId.value])
        .compact();
      const result = await save(toOptionIds(formState, rowsForm.rows.value, groups), toggled);
      // A dirty row keeps its value through a refresh, which would hide a pick the server reconciled away
      rowsForm.revertChanges();
      if (result) {
        rowsForm.update({
          groups: result.config.optionGroups,
          optionIds: idsOf(result.config.options, "readyPlanOption"),
        });
      }
    },
  });

  const rowByRpogId = rowsForm.rows.rows.keyBy((row) => row.groupId.value);
  const rowFor = (rpogId: string) => rowByRpogId[rpogId] ?? fail(`No row for group ${rpogId}`);

  const sections = toSelectOptionsSections(groups, checkoutConfig.interiorSubSteps).map((section) => ({
    title: section.title,
    childSections: section.groups
      .filter((group) => showGroup(group, rowFor(group.group.id).value, showExpired))
      .map((group) => toChildSection(group, groups, rowFor, priceMode)),
  }));

  return (
    <FormSectionLayout
      title="Select Options"
      description="This shows the current options available for this project's product offering. Add, remove, or change options to build the change event."
      withJumpLinks
      withAutoSave
      initialFields={
        <OptionsFilterBar
          showExpired={showExpired}
          onShowExpired={setShowExpired}
          priceMode={priceMode}
          onPriceMode={(mode) => formState.priceMode.set(mode)}
        />
      }
      sections={sections.filter((section) => section.childSections.nonEmpty)}
    />
  );
}

/** The one place that picks a card for a group's shape. */
function toChildSection(
  group: SelectOptionsStep_OptionGroupFragment,
  allGroups: SelectOptionsStep_OptionGroupFragment[],
  rowFor: (rpogId: string) => ObjectState<SelectOptionsFormInput>,
  priceMode: OptionPriceMode,
): PlainFormSectionChild {
  const row = rowFor(group.group.id);
  const base = { id: group.id, title: group.group.name, tag: cutoffTag(group.cutoffDate) };

  const childGroup = group.childGroup && allGroups.find((g) => g.group.id === group.childGroup!.id);
  if (isExteriorGroup(group)) {
    // Elevations and exterior schemes, the main house's and the ADU's: image cards, pick one
    return { ...base, fields: <ExteriorOptionCard group={group} field={row.optionId} priceMode={priceMode} /> };
  } else if (childGroup && isProductOptionGroup(childGroup)) {
    // "Add Decorative Openings" with a product per variant: a checkbox for the add-on plus the variants' thumbnails
    return {
      ...base,
      fields: (
        <SingleOrLinkedOptionCard
          group={group}
          childGroup={childGroup}
          selectedField={row.selected}
          optionField={rowFor(childGroup.group.id).optionId}
          priceMode={priceMode}
        />
      ),
    };
  } else if (childGroup) {
    // "Add Washer and Dryer" with package variants: a checkbox for the add-on plus a radio row per variant
    return personalizationOptionCard({
      group,
      childGroup,
      selectedField: row.selected,
      variantField: rowFor(childGroup.group.id).optionId,
      priceMode,
    });
  } else if (isProductOptionGroup(group)) {
    // Flooring, pendants: a thumbnail per product. A pick-one group like a chandelier has no checkbox
    return {
      ...base,
      fields: (
        <SingleOrLinkedOptionCard
          group={group}
          selectedField={hasToggle(group) ? row.selected : undefined}
          optionField={row.optionId}
          priceMode={priceMode}
        />
      ),
    };
  } else if (group.group.isSingleOptionGroup) {
    // Structural options and add-ons, e.g. "Holiday Lighting": one product with a checkbox
    return {
      ...base,
      fields: <ProductOptionCard group={group} field={row.selected} priceMode={priceMode} />,
      // No tag or title for single-option groups otherwise they'd duplicate the card's content.
      tag: undefined,
      title: "",
    };
  } else {
    // Interior schemes, Spec Level, appliance packages: a radio row per package, pick one
    return packageOptionCard({ group, field: row.optionId, priceMode });
  }
}

type SelectOptionsRowsInput = { groups: SelectOptionsStep_OptionGroupFragment[]; optionIds: Maybe<string[]> };

/**
 * Builds one form row per group (child groups included) from the config's current `optionIds`:
 * - `selected` is whether the group currently has a pick; groups without a checkbox are always `true`;
 * - `optionId` is the current pick, or for toggle groups the first option (the card shows it even while off).
 */
function mapToForm(input: SelectOptionsRowsInput): { rows: SelectOptionsFormInput[] } {
  const { groups, optionIds = [] } = input;
  const optionIdSet = new Set(optionIds);
  const rows = groups.map((ccfgog) => {
    const currentRpoId = ccfgog.options.find((ccfgpo) => optionIdSet.has(ccfgpo.option.id))?.option.id;
    const toggle = hasToggle(ccfgog);
    return {
      groupId: ccfgog.group.id,
      selected: toggle ? currentRpoId !== undefined : true,
      optionId: currentRpoId ?? (toggle ? ccfgog.options.first?.option.id : undefined),
    };
  });
  return { rows };
}

/**
 * The selection the rows save: the change event's whole `optionIds`, replacing the side's selection like checkout does.
 * A row counts when it's `selected` and has an `optionId`, and a child group's row (the "Add Washer and Dryer"
 * variants) only while its parent's row is `selected` too. Ids already selected that belong to no loaded group
 * (`showInCheckout=false` options) are kept.
 */
function toOptionIds(
  formState: ObjectState<AddOrModifyOptionsFormInput>,
  rows: SelectOptionsFormInput[],
  groups: SelectOptionsStep_OptionGroupFragment[],
): string[] {
  const groupByRpogId = groups.keyBy((ccfgog) => ccfgog.group.id);
  const rowByGroupId = rows.keyBy((row) => row.groupId);
  const loadedOptionIds = new Set(groups.flatMap((ccfgog) => ccfgog.options.map((ccfgpo) => ccfgpo.option.id)));
  const picked = rows
    .filter((row) => {
      if (!row.selected || !row.optionId) return false;
      const parentId = groupByRpogId[row.groupId]?.parentGroup?.id;
      return !parentId || !!rowByGroupId[parentId]?.selected;
    })
    .map((row) => row.optionId!);
  const kept = (formState.configChange.optionIds.value ?? []).filter((id) => !loadedOptionIds.has(id));
  return [...kept, ...picked].unique();
}

const formConfig: ObjectConfig<{ rows: SelectOptionsFormInput[] }> = {
  rows: {
    type: "list",
    // `groupId` is the row's identity, so a refresh updates each row in place and the cards' bound fields stay live
    config: { groupId: { type: "value", isIdKey: true }, selected: { type: "value" }, optionId: { type: "value" } },
  },
};
